<!--
  @fileoverview File Uploader Component - Main File Upload Dialog

  Provides file upload functionality for the webchat widget including:
  - Multiple file type support (images, documents)
  - File validation and virus scanning
  - Upload confirmation dialog with preview
  - Customizable disclaimer text

  @component FileUploader
  @displayName File Uploader

  Features:
  - Separate input handlers for images, documents, and general files
  - File preview before upload confirmation
  - Antivirus scanning integration
  - File type validation
  - Upload progress indication via busy state

  @example
  <FileUploader ref="fileUploader" />

  // Trigger upload from parent:
  this.$refs.fileUploader.onUpload('image')
-->
<template>
  <div>
    <input
      :id="fileUploaderId"
      ref="fileUploader"
      type="file"
      class="hidden"
      accept="image/*, .xls, .xlsx, .doc, .docx, .dotx, application/msword, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, .HEIC, .MOV"
      @change="onChangeUpload"
    />

    <input
      :id="imageUploaderId"
      ref="imageUploader"
      type="file"
      class="hidden"
      accept="image/*, .HEIC"
      @change="onChangeUpload"
    />

    <input
      :id="documentUploaderId"
      ref="documentUploader"
      type="file"
      class="hidden"
      accept=".xls, .xlsx, .doc, .docx, .dotx, application/msword, application/pdf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, .MOV"
      @change="onChangeUpload"
    />

    <el-dialog
      width="550px"
      v-model="dialogVisible"
      :modal="false"
      :show-close="false"
      @closed="onDialogClosed"
      append-to-body
    >
      <template #header>File Upload</template>
      <div v-if="settings.fileUploadDisclaimer" class="font-[Arial,sans-serif]">
        <span class="font-bold">Disclaimer:</span>
        <br />
        <span v-html="markHTML(settings.fileUploadDisclaimer, '')"></span>
      </div>

      <br />
      <File
        v-for="(value, index) in confirmationFiles"
        v-model="confirmationFiles[index]"
        :file="pendingUploadFiles[index]"
        :preview="confirmationFilesPreview[index]"
        :key="index"
        @update-is-busy="updateIsBusy"
        @update-antivirus-failed="updateAntivirusFailed"
        @update-is-file-type-invalid="updateIsFileTypeInvalid"
        ref="uploadedFiles"
      />
      <template #footer>
        <span class="dialog-footer">
          <el-button :disabled="isBusy" @click="dialogVisible = false">Cancel</el-button>
          <el-button
            :loading="isBusy"
            :disabled="isInvalid"
            type="primary"
            @click="confirmFileUpload"
          >
            Confirm
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/FileUploader/Index
 * @description Main file upload dialog with preview and validation
 *
 * @vue3.5migration - Uses useId() to prevent ID collisions when multiple webchat widgets
 * are embedded on the same page. Each widget instance gets unique input IDs.
 */
import { ref, computed, onMounted, getCurrentInstance, useId } from "vue";
import { transformFileName } from "@/helpers/validateFile";
import * as _ from "lodash-es";
import File from "./File";
import * as Sentry from "@sentry/browser";
import { useMainStore } from "@/stores/main";
import { markHTML } from "@/filters";

/**
 * Pinia store instance
 */
const store = useMainStore();

/**
 * Get Vue instance for $notify and $device access
 */
const { proxy } = getCurrentInstance();

/**
 * Generate unique IDs for file inputs (Vue 3.5 useId())
 * Prevents ID collisions when multiple webchat widgets are on the same page
 * Each widget instance gets its own unique input IDs
 */
const fileUploaderId = useId();
const imageUploaderId = useId();
const documentUploaderId = useId();

/**
 * Component local state
 */
/** Whether the upload dialog is visible */
const dialogVisible = ref(false);
/** Array of confirmed file names */
const confirmationFiles = ref([]);
/** Array of file preview URLs (blob URLs) */
const confirmationFilesPreview = ref([]);
/** FileList of pending files to upload */
const pendingUploadFiles = ref([]);
/** Whether upload is in progress */
const isBusy = ref(false);
/** Whether antivirus scan failed */
const antivirusFailed = ref(true);
/** Whether file type validation failed */
const isFileTypeInvalid = ref(true);

/**
 * Template refs for file inputs
 */
const fileUploader = ref(null);
const imageUploader = ref(null);
const documentUploader = ref(null);
const uploadedFiles = ref(null);

/**
 * Access webchat settings from Pinia store
 * @returns {Object} Webchat configuration settings
 */
const settings = computed(() => store.settings);

/**
 * Check if all uploaded file components are valid
 * @returns {boolean} True if all files are valid for upload
 */
const canUpload = computed(() => {
  if (!uploadedFiles.value) return false;
  const invalidComponent = uploadedFiles.value.find(
    (uploadedFileComponent) => !uploadedFileComponent.isValid
  );
  return !invalidComponent;
});

/**
 * Check if upload should be disabled
 *
 * Upload is invalid if:
 * - No files are pending
 * - Antivirus scan failed
 * - File type validation failed
 *
 * @returns {boolean} True if upload should be disabled
 */
const isInvalid = computed(() => {
  return _.isEmpty(pendingUploadFiles.value) || antivirusFailed.value || isFileTypeInvalid.value;
});

/**
 * Debounced function to update agent typing indicator
 *
 * Notifies the server when user is typing/uploading to show
 * typing indicator on the agent side.
 */
const updateTypingIndicator = _.debounce(
  function (isTyping) {
    store.updateAgentTypingIndicator(isTyping);
  },
  500,
  { leading: true }
);

/**
 * Update busy state from child File components
 * @param {boolean} value - Whether any file is being processed
 */
function updateIsBusy(value) {
  isBusy.value = value;
}

/**
 * Update antivirus scan status from child File components
 * @param {boolean} value - Whether antivirus scan failed
 */
function updateAntivirusFailed(value) {
  antivirusFailed.value = value;
}

/**
 * Update file type validation status from child File components
 * @param {boolean} value - Whether file type is invalid
 */
function updateIsFileTypeInvalid(value) {
  isFileTypeInvalid.value = value;
}

/**
 * Handle dialog close - reset all state
 *
 * Called when dialog is closed (either by cancel or after upload).
 * Cleans up all temporary state and blob URLs.
 */
function onDialogClosed() {
  dialogVisible.value = false;
  confirmationFiles.value = [];
  confirmationFilesPreview.value = [];
  pendingUploadFiles.value = [];
  isBusy.value = false;
}

/**
 * Trigger file upload dialog for specific type
 *
 * Opens the appropriate file input based on type.
 * Clears previous selection before opening.
 *
 * @param {string} type - Upload type: 'image', 'document', or 'file'
 */
function onUpload(type) {
  switch (type) {
    case "image":
      imageUploader.value.value = null;
      imageUploader.value.click();
      break;
    case "document":
      documentUploader.value.value = null;
      documentUploader.value.click();
      break;
    case "file":
      fileUploader.value.value = null;
      fileUploader.value.click();
      break;
    default:
    //
  }
}

/**
 * Handle file selection change
 *
 * Called when user selects files in any of the file inputs.
 * Creates preview URLs and opens confirmation dialog.
 *
 * @param {Event} event - Input change event with files
 */
function onChangeUpload(event) {
  dialogVisible.value = true;
  const files = event.target.files;

  confirmationFilesPreview.value = _.map(files, (item, index) => {
    return URL.createObjectURL(item);
  });

  confirmationFiles.value = _.map(files, (item, index) => {
    return transformFileName(item.name);
  });

  pendingUploadFiles.value = files;
}

/**
 * Confirm and execute file upload
 *
 * Validates all files, then dispatches upload action to store.
 * Shows typing indicator during upload.
 * Handles errors with user notification.
 */
async function confirmFileUpload() {
  if (!canUpload.value) {
    return;
  }

  isBusy.value = true;
  updateTypingIndicator(true);

  try {
    await store.uploadFile({
      files: pendingUploadFiles.value,
      vm: proxy,
    });
  } catch (err) {
    Sentry.captureException(err);
    proxy.$notify({
      title: "File upload failed",
      type: "error",
    });
  } finally {
    isBusy.value = false;
    dialogVisible.value = false;
    updateTypingIndicator(false);
  }
}

/**
 * Expose methods for parent component access
 */
defineExpose({
  onUpload,
});

/**
 * Listen for global upload events on mount
 *
 * Allows other components to trigger file upload via event bus.
 */
onMounted(() => {
  // Note: In Vue 3, $root.$on is deprecated.
  // Consider using mitt or provide/inject for event communication.
  // For backward compatibility, we expose onUpload method via defineExpose.
});
</script>

<style></style>
